import { ref } from "vue";

export const SESSION_STORAGE_REMOVE_OLD_CARD_FROM_PUNCHOUT_SESSION = "removeOldPunchoutCartAfterAuthentication";
export const LOCAL_STORAGE_CREDENTIALS_SET_FOR_PUNCHOUT_SESSION = "credentialsSetForPunchoutSession";

function getPunchoutCookies(): PublicThorlabsPunchoutDataWithToken | null {
  const cookies = document.cookie.split("; ").reduce((acc: Record<string, string>, curr) => {
    const [key, ...valParts] = curr.split("=");
    const value = valParts.join("="); // preserve "=" and "."
    acc[key] = value;
    return acc;
  }, {});

  // From frontend url, thorlabsToken
  const punchoutThorlabsToken = cookies["punchout_thorlabsToken"] || null;
  // From inital authentication request, punchoutClaims
  const punchoutTradeCentricSessionId = cookies["punchout_tradeCentricSessionId"] || null;
  const currency = cookies["punchout_currency"] || null;
  // From organization details and settings
  const organizationName = cookies["punchout_organizationName"] || null;
  const freeShippingAsString = cookies["punchout_freeShipping"] || null;
  const freeShipping = freeShippingAsString ? freeShippingAsString.toLowerCase() === "true" : false;
  const shippingChargeType =
    cookies["punchout_shippingChargeType"] !== undefined ? Number(cookies["punchout_shippingChargeType"]) : 0;
  const signedInWithFallbackCredentialsAsString = cookies["punchout_signedInWithFallbackCredentials"] || null;
  const signedInWithFallbackCredentials = signedInWithFallbackCredentialsAsString
    ? signedInWithFallbackCredentialsAsString.toLowerCase() === "true"
    : false;

  // Optional values
  const shippingMethodRateModeId = cookies["punchout_shippingMethodRateModeId"] || null;
  const countryCode = cookies["punchout_countryCode"] || null;
  const zipCode = cookies["punchout_zipCode"] || null;
  const hiddenAlerts = cookies["punchout_hiddenAlerts"] || null;

  if (punchoutThorlabsToken && punchoutTradeCentricSessionId && currency && organizationName && freeShippingAsString) {
    return {
      thorlabsPunchoutToken: punchoutThorlabsToken,
      tradeCentricPunchoutSessionId: punchoutTradeCentricSessionId,
      currency: currency,
      organizationName: organizationName,
      freeShippingTL: freeShipping,
      shippingMethodRateModeIdTL: shippingMethodRateModeId,
      shippingChargeTypeTL: shippingChargeType,
      countryCodeTL: countryCode,
      zipCodeTL: zipCode,
      signedInWithFallbackCredentials: signedInWithFallbackCredentials,
      hiddenAlerts: hiddenAlerts,
    } as PublicThorlabsPunchoutDataWithToken;
  }
  return null;
}

export interface PublicThorlabsPunchoutData {
  tradeCentricPunchoutSessionId: string;
  organizationName: string;
  currency: string;
  freeShippingTL: boolean;
  shippingChargeTypeTL: number;
  shippingMethodRateModeIdTL?: string;
  countryCodeTL?: string;
  zipCodeTL?: string;
  signedInWithFallbackCredentials: boolean;
  hiddenAlerts: string;
  redirectToItemSkuFamilyPagePath?: string;
  // Auth data
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface PublicThorlabsPunchoutDataWithToken extends PublicThorlabsPunchoutData {
  thorlabsPunchoutToken: string;
}

export function setPunchoutCookies(
  thorlabsPunchoutToken: string,
  tradeCentricPunchoutSessionId: string,
  currency: string,
  organizationName: string,
  freeShippingTL: boolean,
  shippingChargeTypeTL: number,
  signedInWithFallbackCredentials: boolean,
  shippingMethodRateModeIdTL?: string | null,
  countryCodeTL?: string | null,
  zipCodeTL?: string | null,
): void {
  // Clear existing cookies first to avoid duplicates of old values
  clearPunchoutCookies(false);

  document.cookie = `punchout_thorlabsToken=${thorlabsPunchoutToken}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_tradeCentricSessionId=${tradeCentricPunchoutSessionId}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_currency=${currency}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_organizationName=${organizationName}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_freeShipping=${freeShippingTL}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_shippingChargeType=${shippingChargeTypeTL}; path=/; SameSite=None; Secure; Partitioned`;
  document.cookie = `punchout_signedInWithFallbackCredentials=${signedInWithFallbackCredentials}; path=/; SameSite=None; Secure; Partitioned`;
  if (shippingMethodRateModeIdTL !== null) {
    document.cookie = `punchout_shippingMethodRateModeId=${shippingMethodRateModeIdTL}; path=/; SameSite=None; Secure; Partitioned`;
  }
  if (countryCodeTL !== null) {
    document.cookie = `punchout_countryCode=${countryCodeTL}; path=/; SameSite=None; Secure; Partitioned`;
  }
  if (zipCodeTL !== null) {
    document.cookie = `punchout_zipCode=${zipCodeTL}; path=/; SameSite=None; Secure; Partitioned`;
  }
  document.cookie = `punchout_hiddenAlerts=; path=/; SameSite=None; Secure; Partitioned`;

  punchoutCookies.value = getPunchoutCookies();
}

export function clearPunchoutCookies(updateValue: boolean = true): void {
  // The cookies will be removed if they have an expiration date in the past
  document.cookie =
    "punchout_thorlabsToken=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_tradeCentricSessionId=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_currency=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_organizationName=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_freeShipping=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_shippingMethodRateModeId=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_shippingChargeType=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_countryCode=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_zipCode=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_signedInWithFallbackCredentials=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "punchout_hiddenAlerts=; path=/; SameSite=None; Secure; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 GMT";

  if (updateValue) {
    punchoutCookies.value = getPunchoutCookies();
  }
}

export function checkCorrectPunchoutTokenData(punchoutTokenData: PublicThorlabsPunchoutData) {
  return (
    punchoutTokenData &&
    // Authentication details must be present
    punchoutTokenData.access_token !== null &&
    punchoutTokenData.access_token !== undefined &&
    punchoutTokenData.refresh_token !== null &&
    punchoutTokenData.refresh_token !== undefined &&
    punchoutTokenData.expires_in !== null &&
    punchoutTokenData.expires_in !== undefined &&
    punchoutTokenData.token_type !== null &&
    punchoutTokenData.token_type !== undefined &&
    // Punchout details must be present
    punchoutTokenData.tradeCentricPunchoutSessionId !== null &&
    punchoutTokenData.tradeCentricPunchoutSessionId !== undefined &&
    punchoutTokenData.currency !== null &&
    punchoutTokenData.currency !== undefined &&
    punchoutTokenData.organizationName !== null &&
    punchoutTokenData.organizationName !== undefined &&
    punchoutTokenData.freeShippingTL !== null &&
    punchoutTokenData.freeShippingTL !== undefined &&
    punchoutTokenData.shippingChargeTypeTL !== null &&
    punchoutTokenData.shippingChargeTypeTL !== undefined &&
    punchoutTokenData.signedInWithFallbackCredentials !== null &&
    punchoutTokenData.signedInWithFallbackCredentials !== undefined
  );
}

export function updateHiddenAlertsInPunchoutCookies(hiddenAlerts: string): void {
  document.cookie = `punchout_hiddenAlerts=${hiddenAlerts}; path=/; SameSite=None; Secure; Partitioned`;

  // Update the reactive value
  punchoutCookies.value = getPunchoutCookies();
}

export const punchoutCookies = ref<PublicThorlabsPunchoutDataWithToken | null>(getPunchoutCookies());
